import type { Editor } from "@tiptap/react";
import type { Node as ProsemirrorNode, ResolvedPos } from "prosemirror-model";
import type { Selection } from "prosemirror-state";
import type { EditorView } from "prosemirror-view";

import { useCurrentChatContext } from "@editor/contexts/CurrentChatContext";

import { serializeChatTokenFromNode } from "./chatTokenSerialization";

interface MarkdownStorage {
  getMarkdown: () => string;
}

type MarkdownEnabledEditor = Editor & {
  storage: Editor["storage"] & {
    markdown?: MarkdownStorage;
  };
};

const hasMarkdownStorage = (
  editor: Editor,
): editor is MarkdownEnabledEditor => {
  return "markdown" in editor.storage;
};

const serializeNodeForPrompt = (node: ProsemirrorNode) => {
  if (node.type.name === "text") {
    return node.text ?? "";
  }

  if (node.type.name === "hardBreak") {
    return "<br />";
  }

  const serializedToken = serializeChatTokenFromNode(node);
  return serializedToken ?? "";
};

// NOTE (Bobby, 2025-06-30): This function takes the tip tap editor state and builds an easier string to work with both for the llm and for us to parse + render
// components with.
export const getCleanedChatPrompt = (editor: Editor) => {
  if (hasMarkdownStorage(editor)) {
    return editor.storage.markdown?.getMarkdown() ?? "";
  }

  let result = "";
  editor.state.doc.descendants((node) => {
    result += serializeNodeForPrompt(node);
  });

  return result;
};

export const chatEditorOnUpdate = (
  setAtPosition: (position: number | null) => void,
  atPosition: number | null,
) => {
  const { isContextMenuOpen, dispatchCurrentChat } = useCurrentChatContext();

  return ({ editor }: { editor: Editor }) => {
    const content = editor.getText();
    const cursorPos = editor.state.selection.anchor;

    // Check if @ was just typed
    if (content[cursorPos - 1] === "@" && !isContextMenuOpen) {
      setAtPosition(cursorPos - 1);
      dispatchCurrentChat({ type: "openContextMenu" });
    }

    // Check if @ was deleted from the tracked position
    if (atPosition !== null && content[atPosition] !== "@") {
      dispatchCurrentChat({ type: "closeContextMenu" });
      setAtPosition(null);
    }
  };
};

const selectionHasResolvedPos = (
  selection: Selection,
): selection is Selection & { $from: ResolvedPos } => {
  return "$from" in selection;
};

export const chatEditorHandleKeyDown = (
  setAtPosition: (position: number | null) => void,
  atPosition: number | null,
  onSubmit: (event?: React.FormEvent) => Promise<void>,
) => {
  const { isContextMenuOpen, dispatchCurrentChat } = useCurrentChatContext();

  return (view: EditorView, event: KeyboardEvent) => {
    const { state } = view;
    const { selection } = state;

    const isInNode = (nodeNames: string[]) => {
      if (!selectionHasResolvedPos(state.selection)) {
        return false;
      }
      const { $from } = state.selection;
      for (let depth = $from.depth; depth > 0; depth -= 1) {
        const node = $from.node(depth);
        if (nodeNames.includes(node.type.name)) {
          return true;
        }
      }
      return false;
    };

    const isInList = isInNode(["listItem"]);
    const isInCodeBlock = isInNode(["codeBlock"]);

    if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      void onSubmit();
      return true;
    }

    if (event.key === "Enter" && !event.shiftKey && !isContextMenuOpen) {
      // Allow native list/code behavior inside lists or code blocks
      if (isInList || isInCodeBlock) {
        return false;
      }
      event.preventDefault();
      void onSubmit();
      return true;
    }

    // Handle @ key
    if (event.key === "@" && !isContextMenuOpen) {
      setTimeout(() => {
        setAtPosition(selection.anchor);
        dispatchCurrentChat({ type: "openContextMenu" });
      }, 0);
    }

    // Handle Escape to close menu
    if (event.key === "Escape" && isContextMenuOpen) {
      dispatchCurrentChat({ type: "closeContextMenu" });
      setAtPosition(null);
      event.preventDefault();
      return true;
    }

    // Handle Backspace - check if we're deleting the @
    if (event.key === "Backspace" && isContextMenuOpen && atPosition !== null) {
      const textBefore = state.doc.textBetween(atPosition, atPosition + 1);
      if (textBefore === "@") {
        // We're about to delete the @, close the menu
        dispatchCurrentChat({ type: "closeContextMenu" });
        setAtPosition(null);
      }
    }

    // Handle Space to close menu without selection
    if (event.key === " " && isContextMenuOpen) {
      dispatchCurrentChat({ type: "closeContextMenu" });
      setAtPosition(null);
    }

    return false;
  };
};

/**
 * Returns a handler we can pass to tiptap to handle pasting images in chat.
 *
 * Basically just validates the images and calls setFiles if they're valid.
 */
export const chatEditorHandlePaste = (
  setFiles: React.Dispatch<React.SetStateAction<File[] | null>>,
) => {
  return (_view: EditorView, event: ClipboardEvent) => {
    const clipboardData = event.clipboardData;
    if (clipboardData && clipboardData.files.length > 0) {
      const files = Array.from(clipboardData.files);
      const imageFiles = files.filter((file) => file.type.startsWith("image/"));

      if (imageFiles.length > 0) {
        setFiles((currentFiles) => {
          if (currentFiles === null) {
            return imageFiles;
          }
          return [...currentFiles, ...imageFiles];
        });

        event.preventDefault();
        return true;
      }
    }

    return false;
  };
};
